import {
  HomeOutlined,
  CalendarOutlined,
  UserOutlined,
  ShopOutlined,
  UsergroupDeleteOutlined,
  ClusterOutlined,
  UnlockOutlined,
  PoweroffOutlined,
  TagOutlined,
  SettingOutlined,
  BarChartOutlined,
  NotificationOutlined,
  ReconciliationOutlined,
  WhatsAppOutlined,
  BellOutlined,
  TransactionOutlined,
  CalculatorOutlined,
  FlagOutlined,
  PercentageOutlined,
  CoffeeOutlined,
} from '@ant-design/icons'
import { createRouteForLink } from '@/utils/helpers/urlHelper'
import { App, Tooltip, type MenuProps } from 'antd'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { pageUrl } from '@/constants/url'
import { useCallback, useMemo } from 'react'
import { ErrorApi } from '@/types/common'
import { api } from '@/utils/api'
import { apiSignOut } from '@/utils/api/url'
import {
  attendancesPermissions,
  billingPermissions,
  clientPermissions,
  commissionTipsPermissions,
  dealPermissions,
  expensesPermissions,
  notificationPermissions,
  packagePermissions,
  paymentPermissions,
  productPermissions,
  reportPermissions,
  rolePermissions,
  salesPermissions,
  schedulePermissions,
  servicePermissions,
  settingPermissions,
  userPermissions,
  voucherPermissions,
} from '@/constants/permissions'
import { useTranslation } from 'react-i18next'

import { usePermissions } from './usePermissions'
import { useGetBranchDetail } from '../branches/useGetBranchDetail'
import { ACCESS_TOKEN_LOCAL_STORAGE } from '@/constants/localStorageKeys'
import { handleChangeLanguage } from '@/utils/helpers/translations'
import { useAccess } from './useAccess'
import { useOnboardingStore } from '@/store/useOnboarding'

type MenuItem = Required<MenuProps>['items'][number]

interface IGenerateLink {
  label: string
  url?: string
  haveAccess?: boolean
}
const messageKey = 'logout-message'
export const useMenu = () => {
  const showLanguage = true
  const { message } = App.useApp()
  const { havePermissionAccess } = usePermissions()
  const { companyProfile } = useOnboardingStore()
  const { allowAccess, attendanceEnabled, vouchersEnabled, leaveEnabled } =
    useAccess()
  const haveOutStandingBilling = useMemo(() => {
    if (allowAccess !== undefined) {
      return !allowAccess
    }
    return false
  }, [allowAccess])
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const { data: branchData } = useGetBranchDetail({
    branch: params.branch || '',
  })
  const handleLogout = useCallback(async () => {
    try {
      message.loading({
        key: messageKey,
        content: t('common.logging.out'),
        duration: 0,
      })
      await api.post(apiSignOut)
      localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE)
      navigate(pageUrl.signIn)
      message.success({
        key: messageKey,
        content: t('common.logged.out'),
      })
    } catch (error) {
      message.destroy(messageKey)
      const err = error as ErrorApi
      throw err
    }
  }, [navigate])
  const MenuLink = ({ label, url, haveAccess }: IGenerateLink) => {
    if (haveOutStandingBilling) {
      return label
    }
    if (haveAccess === false)
      return <Tooltip title={t('common.no.access')}>{label}</Tooltip>
    if (!url) return label
    return <Link to={url}>{label}</Link>
  }

  const topMenus: MenuItem[] = useMemo(
    () => [
      companyProfile?.name
        ? {
            key: 'branch',
            icon: <ClusterOutlined />,
            label: (
              <MenuLink
                label={t('common.branches')}
                url={pageUrl.branchSetting}
              />
            ),
          }
        : null,
      {
        key: 'account',
        icon: <UserOutlined />,
        label: t('common.my.account'),
        children: [
          {
            key: 'account-profile',
            icon: <UserOutlined />,
            label: (
              <MenuLink
                label={t('common.profile')}
                url={pageUrl.accountProfile}
              />
            ),
          },
          {
            key: 'account-password',
            icon: <UnlockOutlined />,
            label: (
              <MenuLink
                label={t('common.password.security')}
                url={pageUrl.accountPasswordSecurity}
              />
            ),
          },
          {
            label: (
              <MenuLink
                label={t('common.push.notification')}
                url={pageUrl.pushNotification}
              />
            ),
            key: pageUrl.pushNotification,
            icon: <BellOutlined />,
          },
          {
            label: (
              <MenuLink
                label={t('common.my.commission')}
                url={pageUrl.myCommissions}
              />
            ),
            key: pageUrl.myCommissions,
            icon: <PercentageOutlined />,
          },
          ...(leaveEnabled
            ? [
                {
                  label: (
                    <MenuLink
                      label={t('common.my.leave')}
                      url={pageUrl.accountMyLeave}
                    />
                  ),
                  key: pageUrl.accountMyLeave,
                  icon: <CoffeeOutlined />,
                },
              ]
            : []),
          {
            key: 'account-logout',
            label: t('common.logout'),
            icon: <PoweroffOutlined />,
            onClick: handleLogout,
            style: {
              color: 'red',
            },
          },
        ],
      },
      ...(showLanguage
        ? [
            {
              key: 'language',
              icon: <FlagOutlined />,
              label: t('common.language'),
              children: [
                {
                  key: 'EN',
                  label: 'English',
                  onClick: () => handleChangeLanguage('en'),
                },
                {
                  key: 'ID',
                  label: 'Indonesia',
                  onClick: () => handleChangeLanguage('id'),
                },
              ],
            },
          ]
        : []),
    ],
    [MenuLink, handleLogout, showLanguage, companyProfile?.name, t, leaveEnabled],
  )
  const menus = useMemo((): MenuItem[] => {
    const mernuList = [
      {
        key: '/',
        icon: <HomeOutlined />,
        label: (
          <MenuLink
            label={t('common.home')}
            url={createRouteForLink(pageUrl.home)}
          />
        ),
      },
      {
        key: '/calendar',
        icon: <CalendarOutlined />,
        id: 'calendar-menu',
        label: (
          <MenuLink
            label={t('menu.calendar')}
            url={createRouteForLink(pageUrl.calendar)}
          />
        ),
      },
      {
        key: 'sales',
        icon: <TagOutlined />,
        label: t('menu.sales'),
        children: [
          {
            disabled: !havePermissionAccess({
              permission: salesPermissions.read,
            }),
            key: '/sales',
            label: (
              <MenuLink
                label={t('menu.checkout')}
                url={createRouteForLink(pageUrl.sales)}
                haveAccess={havePermissionAccess({
                  permission: salesPermissions.read,
                })}
              />
            ),
          },
          {
            key: '/sales/all',
            label: (
              <MenuLink
                label={t('menu.allSales')}
                url={createRouteForLink(pageUrl.salesAll)}
              />
            ),
          },
        ],
      },
      {
        key: '/clients',
        icon: <UserOutlined />,
        disabled: !havePermissionAccess({
          permission: clientPermissions.read,
        }),
        label: (
          <MenuLink
            label={t('menu.clientsList')}
            url={createRouteForLink(pageUrl.client)}
            haveAccess={havePermissionAccess({
              permission: clientPermissions.read,
            })}
          />
        ),
      },
      {
        key: '/catalog',
        label: t('menu.catalog'),
        icon: <ShopOutlined />,
        children: [
          {
            key: '/services',
            id: 'services-menu',
            disabled: !havePermissionAccess({
              permission: servicePermissions.read,
            }),
            label: (
              <MenuLink
                label={t('menu.services')}
                url={createRouteForLink(pageUrl.services, params.branch)}
                haveAccess={havePermissionAccess({
                  permission: servicePermissions.read,
                })}
              />
            ),
          },
          {
            key: '/packages',
            id: 'package-menu',
            disabled: !havePermissionAccess({
              permission: packagePermissions.read,
            }),
            label: (
              <MenuLink
                label={t('menu.package')}
                url={createRouteForLink(pageUrl.packages, params.branch)}
                haveAccess={havePermissionAccess({
                  permission: packagePermissions.read,
                })}
              />
            ),
          },
          {
            key: '/products',
            id: 'products-menu',
            disabled: !havePermissionAccess({
              permission: productPermissions.read,
            }),
            label: (
              <MenuLink
                label={t('menu.product')}
                url={createRouteForLink(pageUrl.products, params.branch)}
                haveAccess={havePermissionAccess({
                  permission: productPermissions.read,
                })}
              />
            ),
          },
          {
            key: '/vouchers',
            id: 'vouchers-menu',
            disabled:
              !havePermissionAccess({
                permission: voucherPermissions.read,
              }) || !vouchersEnabled,
            label: (
              <MenuLink
                label="Vouchers"
                url={createRouteForLink(pageUrl.vouchers, params.branch)}
                haveAccess={
                  havePermissionAccess({
                    permission: voucherPermissions.read,
                  }) && vouchersEnabled
                }
              />
            ),
          },
        ],
      },

      {
        key: '/deals',
        icon: <NotificationOutlined />,
        disabled: !havePermissionAccess({
          permission: dealPermissions.read,
        }),
        label: (
          <MenuLink
            haveAccess={havePermissionAccess({
              permission: dealPermissions.read,
            })}
            label={t('menu.deals')}
            url={createRouteForLink(pageUrl.deals, params.branch)}
          />
        ),
      },
      ...(branchData?.paymentConfigId &&
      havePermissionAccess({
        permission: paymentPermissions.read,
      })
        ? [
            {
              key: '/payments',
              icon: <TransactionOutlined />,
              disabled: !havePermissionAccess({
                permission: notificationPermissions.read,
              }),
              label: (
                <MenuLink
                  haveAccess={havePermissionAccess({
                    permission: notificationPermissions.read,
                  })}
                  label="Payments"
                  url={createRouteForLink(pageUrl.payments, params.branch)}
                />
              ),
            },
          ]
        : []),
      {
        key: '/notifications',
        icon: <WhatsAppOutlined />,
        disabled: !havePermissionAccess({
          permission: notificationPermissions.read,
        }),
        label: (
          <MenuLink
            haveAccess={havePermissionAccess({
              permission: notificationPermissions.read,
            })}
            label="Notifications"
            url={createRouteForLink(pageUrl.notifications, params.branch)}
          />
        ),
      },
      {
        key: '/expenses',
        icon: <CalculatorOutlined />,
        disabled: !havePermissionAccess({
          permission: expensesPermissions.read,
        }),
        label: (
          <MenuLink
            haveAccess={havePermissionAccess({
              permission: expensesPermissions.read,
            })}
            label={t('menu.expenses')}
            url={createRouteForLink(pageUrl.expenses, params.branch)}
          />
        ),
      },
      {
        key: '/report',
        label: (
          <MenuLink
            haveAccess={
              havePermissionAccess({
                permission: reportPermissions.read,
              }) ||
              havePermissionAccess({
                permission: commissionTipsPermissions.read,
              })
            }
            label={t('menu.reports')}
          />
        ),
        icon: <BarChartOutlined />,
        disabled:
          !havePermissionAccess({ permission: reportPermissions.read }) &&
          !havePermissionAccess({ permission: commissionTipsPermissions.read }),
        children: [
          {
            key: '/report/sales-log',
            disabled: !havePermissionAccess({
              permission: reportPermissions.read,
            }),
            label: (
              <MenuLink
                haveAccess={havePermissionAccess({
                  permission: reportPermissions.read,
                })}
                label={t('menu.salesLog')}
                url={createRouteForLink(pageUrl.salesLog)}
              />
            ),
          },
          {
            key: '/report/tips',
            disabled: !havePermissionAccess({
              permission: commissionTipsPermissions.read,
            }),
            label: (
              <MenuLink
                haveAccess={havePermissionAccess({
                  permission: commissionTipsPermissions.read,
                })}
                label={t('menu.tips')}
                url={createRouteForLink(pageUrl.reportTips)}
              />
            ),
          },
          {
            key: '/report/comissions',
            disabled: !havePermissionAccess({
              permission: commissionTipsPermissions.read,
            }),
            label: (
              <MenuLink
                haveAccess={havePermissionAccess({
                  permission: commissionTipsPermissions.read,
                })}
                label={t('menu.commissions')}
                url={createRouteForLink(pageUrl.reportComissions)}
              />
            ),
          },
          {
            key: pageUrl.reportPerformance,
            disabled: !havePermissionAccess({
              permission: reportPermissions.read,
            }),
            label: (
              <MenuLink
                label={t('menu.performance')}
                url={createRouteForLink(pageUrl.reportPerformance)}
              />
            ),
          },
          {
            key: pageUrl.financeSummary,
            disabled: !havePermissionAccess({
              permission: reportPermissions.read,
            }),
            label: (
              <MenuLink
                label={t('menu.financeSummary')}
                url={createRouteForLink(pageUrl.financeSummary)}
                haveAccess={havePermissionAccess({
                  permission: reportPermissions.read,
                })}
              />
            ),
          },
        ],
      },
      {
        key: '/teams',
        label: t('menu.team'),
        id: 'team-menu',
        icon: <UsergroupDeleteOutlined />,
        children: [
          {
            key: '/teams/users',
            disabled: !havePermissionAccess({
              permission: userPermissions.read,
            }),
            label: (
              <MenuLink
                haveAccess={havePermissionAccess({
                  permission: userPermissions.read,
                })}
                label={t('menu.users')}
                url={createRouteForLink(pageUrl.teams)}
              />
            ),
          },
          {
            key: '/teams/roles',
            disabled: !havePermissionAccess({
              permission: rolePermissions.read,
            }),
            label: (
              <MenuLink
                haveAccess={havePermissionAccess({
                  permission: rolePermissions.read,
                })}
                label={t('menu.roles')}
                url={createRouteForLink(pageUrl.role)}
              />
            ),
          },
          {
            key: '/teams/schedule',
            disabled: !havePermissionAccess({
              permission: schedulePermissions.read,
            }),
            label: (
              <MenuLink
                haveAccess={havePermissionAccess({
                  permission: schedulePermissions.read,
                })}
                label={t('menu.schedule')}
                url={createRouteForLink(pageUrl.teamsSchedule)}
              />
            ),
          },
          ...(attendanceEnabled
            ? [
                {
                  key: '/teams/attendances',
                  disabled: !havePermissionAccess({
                    permission: attendancesPermissions.read,
                  }),
                  label: (
                    <MenuLink
                      haveAccess={havePermissionAccess({
                        permission: attendancesPermissions.read,
                      })}
                      label={t('menu.attendance')}
                      url={createRouteForLink(pageUrl.teamsAttendance)}
                    />
                  ),
                },
              ]
            : []),
        ],
      },
      {
        key: 'setting',
        icon: <SettingOutlined />,
        label: t('menu.settings'),
        children: [
          {
            key: '/settings',
            disabled: !havePermissionAccess({
              permission: settingPermissions.read,
            }),
            label: (
              <MenuLink
                haveAccess={havePermissionAccess({
                  permission: settingPermissions.read,
                })}
                label={t('menu.branchSettings')}
                url={createRouteForLink(pageUrl.settingsBranch)}
              />
            ),
          },
          {
            disabled: !havePermissionAccess({
              permission: settingPermissions.read,
            }),
            key: '/settings/general',
            label: (
              <MenuLink
                haveAccess={havePermissionAccess({
                  permission: settingPermissions.read,
                })}
                label={t('menu.generalSettings')}
                url={pageUrl.settings}
              />
            ),
          },
        ],
      },
      {
        key: '/billings',
        icon: <ReconciliationOutlined />,
        disabled: !havePermissionAccess({
          permission: billingPermissions.read,
        }),
        label: (
          <MenuLink
            label={t('menu.billings')}
            url={createRouteForLink(pageUrl.billing, params.branch)}
            haveAccess={havePermissionAccess({
              permission: billingPermissions.read,
            })}
          />
        ),
      },
    ]
    return mernuList.reduce((acc, el) => {
      const safeChildren = (el.children || []).filter(
        (child) => !child.disabled,
      )

      if (!safeChildren.length && !!el.children) {
        return acc
      }
      if (el.children) {
        return [...acc, { ...el, children: safeChildren }]
      }
      if (el.disabled) {
        return acc
      }
      return [...acc, el]
    }, [] as MenuItem[])
  }, [
    params.branch,
    haveOutStandingBilling,
    t,
    havePermissionAccess,
    branchData,
    attendanceEnabled,
  ])
  return { menus, topMenus, haveOutStandingBilling }
}
